/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { Calendar, DatePicker } from 'sarsaparilla';
import { gnoment, calendarDateToGnoment, gnomentToCalendarDate } from 'shared-ui/gnoment';

import * as types from '../../constants';
import {
    isDayBeingBlockedByLottery,
    isDayManagedByLottery,
    isDayBlocked,
    isDayWalkupOnly,
    AVAILABILITY_PROPTYPES,
} from '../../utils/availability-utils-new';

// Note: this file is the one used by EXTERNAL permits
export default class SingleDatePickerCalendar extends React.Component {
    static propTypes = {
        ...AVAILABILITY_PROPTYPES,
    };

    static defaultProps = {
        ignoreQuotaRestrictions: false,
        calendarOnly: false,
        blockNonLotteryDates: false,
        blockLotteryDates: false,
        currentDate: undefined,
        permit: {},
        dateMap: {},
        availabilityMap: undefined,
        selectedDate: {},
        handleDateChange: () => true,
        id: undefined,
        readOnly: undefined,
        disabled: undefined,
        renderLabels: true,
        initialVisibleMonth: gnoment().utc(),
    };

    getDayType = (day) => {
        if (isDayWalkupOnly(day, this.props)) {
            return 'walkup';
        }
        if (isDayManagedByLottery(day, this.props)) {
            return 'lottery';
        }
        if (isDayBlocked(day, this.props)) {
            return 'blocked';
        }
        return 'available';
    };

    getTooltipContent = (day) => {
        let tooltip = 'Available';
        if (isDayWalkupOnly(day, this.props)) {
            return ['This day is walkup only.'];
        }
        if (isDayManagedByLottery(day, this.props)) {
            if (isDayBlocked(day, this.props)) {
                tooltip = ['This lottery day is unavailable'];
            }
            if (isDayBeingBlockedByLottery(day, this.props)) {
                tooltip = [
                    'This day is being managed by the lottery. Please refer to important dates for application process.',
                ];
            }
            return tooltip;
        }
        if (isDayBlocked(day, this.props)) {
            return 'Unavailable';
        }

        const tooltips = ['Available'];
        if (!this.props.availabilityMap) {
            return tooltips;
        }

        const date = gnoment(day).utc().startOf('day').format();

        for (const quota in this.props.quotas) {
            if (Object.hasOwn(this.props.quotas, quota)) {
                let { remaining = 0, total = 0 } = get(
                    this.props,
                    `quotas[${quota}][${date}]`,
                    {}
                );
                if (remaining > 99) {
                    remaining = '99+';
                } else if (remaining < 0) {
                    remaining = 0;
                }
                if (total > 99) {
                    total = '99+';
                } else if (total < 0) {
                    total = 0;
                }

                const remainingTotalString = `${remaining} out of ${total}`;
                if (
                    quota === types.GROUP_AVAILABILITY_KEY_NAME ||
                    quota === types.GROUP_AVAILABILITY_DAILY_KEY_NAME
                ) {
                    tooltips.push(`\u000AGroups: ${remainingTotalString}`);
                }
                if (quota === types.MEMBER_AVAILABILITY_KEY_NAME) {
                    tooltips.push(`\u000APeople: ${remainingTotalString}`);
                }
                if (quota === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
                    tooltips.push(`\u000AWatercraft: ${remainingTotalString}`);
                }
                if (quota === types.MAX_STOCK_KEY_NAME) {
                    tooltips.push(`\u000AStock: ${remainingTotalString}`);
                }
            }
        }

        return tooltips.map((t, i) => (
            <div className="tooltip-text" key={t}>
                {t}
                {i !== tooltips.length - 1 && <span className="rec-sr-only">,</span>}
            </div>
        ));
    };

    render() {
        if (this.props.ignoreQuotaRestrictions) {
            // Keeping the methods here, since isDayBlocked change doesn't seem to trigger a re-render on DatePicker
            // Was a problem when moving the isDayBlocked method from `LEGACY_DayPickerSingleDateControllerWithKey`
            // Ex. when this.props passed into isDayBlocked changes, there's no update to the calendar
            const isDayBlockedIQR = (day) =>
                !this.props.dateMap[day.format(types.AVAILABILITY_KEY_MAP_FORMAT)];
            const getDayTypeIQR = (day) =>
                isDayBlockedIQR(day) ? 'blocked' : 'available';
            return (
                <DatePicker
                    id={this.props.id ? this.props.id : 'selectedDateId'}
                    value={
                        !isEmpty(this.props.dateMap)
                            ? gnomentToCalendarDate(this.props.selectedDate)
                            : null
                    }
                    onChange={(date) =>
                        date &&
                        this.props.handleDateChange(
                            calendarDateToGnoment(date).utc(true),
                            this.props.id
                        )
                    }
                    numVisibleMonths={1}
                    isDateUnavailable={(day) =>
                        isDayBlockedIQR(calendarDateToGnoment(day))
                    }
                    getDayType={(day) => getDayTypeIQR(calendarDateToGnoment(day))}
                    placeholderValue={gnomentToCalendarDate(
                        this.props.initialVisibleMonth.utc()
                    )}
                    showClearDates={false}
                />
            );
        }

        if (this.props.calendarOnly) {
            return (
                <Calendar
                    numVisibleMonths={1}
                    value={gnomentToCalendarDate(this.props.selectedDate)}
                    onChange={(selectedDate) =>
                        selectedDate &&
                        this.props.handleDateChange(
                            calendarDateToGnoment(selectedDate).utc(true)
                        )
                    }
                    isDateUnavailable={(day) =>
                        isDayBlocked(calendarDateToGnoment(day), this.props)
                    }
                    getDayType={(day) => this.getDayType(calendarDateToGnoment(day))}
                    defaultFocusedValue={gnomentToCalendarDate(
                        this.props.initialVisibleMonth.utc()
                    )}
                    getTooltipContent={(day) =>
                        this.getTooltipContent(calendarDateToGnoment(day))
                    }
                />
            );
        }

        return (
            <DatePicker
                id={this.props.id ? this.props.id : 'selectedDateId'}
                value={
                    this.props.selectedDate
                        ? gnomentToCalendarDate(this.props.selectedDate)
                        : null
                }
                onChange={(date) =>
                    date &&
                    this.props.handleDateChange(
                        calendarDateToGnoment(date).utc(true),
                        this.props.id
                    )
                }
                numVisibleMonths={1}
                isDateUnavailable={(day) =>
                    isDayBlocked(calendarDateToGnoment(day), this.props)
                }
                getDayType={(day) => this.getDayType(calendarDateToGnoment(day))}
            />
        );
    }
}
